import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Box, ArrowLeft, Plus, Edit2, Save, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import { EquipmentFilters, EquipmentForm, EquipmentGrid, EquipmentStats, getModeColor } from '@/components/equipment';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  name: '',
  code: '',
  mode: 'ocean',
  category: '',
  dimensions: { length_cm: 0, width_cm: 0, height_cm: 0 },
  capacity: { max_weight_kg: 0, max_volume_cbm: 0 },
  recommended_use: [],
  restrictions: [],
  image_url: ''
};

const EquipmentManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  // Data state
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  
  // Dialog state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);

  // Fetch equipment
  const fetchEquipment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/equipment`);
      setEquipment(response.data || []);
    } catch (error) {
      console.error('Failed to fetch equipment:', error);
      toast.error('Failed to load equipment');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter equipment
  const filterEquipment = useCallback(() => {
    let filtered = [...equipment];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(e => 
        e.name?.toLowerCase().includes(term) ||
        e.code?.toLowerCase().includes(term) ||
        e.category?.toLowerCase().includes(term)
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(e => e.category === categoryFilter);
    }

    if (modeFilter !== 'all') {
      filtered = filtered.filter(e => e.mode === modeFilter);
    }

    setFilteredEquipment(filtered);
  }, [equipment, searchTerm, categoryFilter, modeFilter]);

  // Auth check
  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user && user.role !== 'admin' && user.role !== 'employee') {
      toast.error('Access denied');
      navigate('/');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  useEffect(() => {
    filterEquipment();
  }, [filterEquipment]);

  const categories = [...new Set(equipment.map(e => e.category))];
  const modes = [...new Set(equipment.map(e => e.mode))];

  // Form handlers
  const resetForm = () => setFormData(INITIAL_FORM);

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const openEditDialog = (item) => {
    setFormData({
      name: item.name || '',
      code: item.code || '',
      mode: item.mode || 'ocean',
      category: item.category || '',
      dimensions: item.dimensions || { length_cm: 0, width_cm: 0, height_cm: 0 },
      capacity: item.capacity || { max_weight_kg: 0, max_volume_cbm: 0 },
      recommended_use: item.recommended_use || [],
      restrictions: item.restrictions || [],
      image_url: item.image_url || ''
    });
    setSelectedItem(item);
    setShowEditDialog(true);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.code || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/equipment`, formData);
      toast.success('Equipment created successfully');
      setShowCreateDialog(false);
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create equipment');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name || !formData.code) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/equipment/${selectedItem.id}`, formData);
      toast.success('Equipment updated successfully');
      setShowEditDialog(false);
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update equipment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await axios.delete(`${API}/admin/equipment/${itemToDelete.id}`);
      toast.success('Equipment deleted successfully');
      setShowDeleteAlert(false);
      setItemToDelete(null);
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete equipment');
    }
  };

  const handleSeedEquipment = async () => {
    try {
      const response = await axios.post(`${API}/admin/equipment/seed`);
      toast.success(response.data.message);
      fetchEquipment();
    } catch (error) {
      toast.error('Failed to seed equipment');
    }
  };

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setShowDetails(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteAlert(true);
  };

  return (
    <div className="min-h-screen bg-steel-50" data-testid="equipment-management">
      {/* Header */}
      <div className="bg-navy-800 pt-24 pb-8">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/admin')}
                className="text-steel-400 hover:text-white mb-4 -ml-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('common.back')}
              </Button>
              <div className="flex items-center gap-3 mb-2">
                <Box className="w-8 h-8 text-gold" />
                <h1 className="font-outfit text-3xl font-bold text-white">{t('equipmentMgmt.title')}</h1>
              </div>
              <p className="text-steel-300">{t('equipmentMgmt.subtitle')}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={fetchEquipment}
                className="border-steel-500 text-white hover:bg-navy-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('common.refresh')}
              </Button>
              {user?.role === 'admin' && (
                <Button 
                  variant="outline" 
                  onClick={handleSeedEquipment}
                  className="border-steel-500 text-white hover:bg-navy-700"
                >
                  {t('equipmentMgmt.seedData')}
                </Button>
              )}
              <Button onClick={openCreateDialog} className="btn-accent">
                <Plus className="w-4 h-4 mr-2" />
                {t('equipmentMgmt.addEquipment')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Stats */}
        <EquipmentStats equipment={equipment} t={t} />

        {/* Filters */}
        <EquipmentFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          modeFilter={modeFilter}
          setModeFilter={setModeFilter}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          modes={modes}
          categories={categories}
          t={t}
        />

        {/* Equipment Grid */}
        <EquipmentGrid
          equipment={filteredEquipment}
          loading={loading}
          onOpenCreate={openCreateDialog}
          onEdit={openEditDialog}
          onDelete={handleDeleteClick}
          onViewDetails={handleViewDetails}
          t={t}
        />
      </div>

      {/* Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl">
          {selectedItem && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Box className="w-5 h-5 text-gold" />
                  {selectedItem.name}
                  <Badge className={getModeColor(selectedItem.mode)} variant="outline">{selectedItem.mode}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-steel-50 rounded-lg">
                    <p className="text-xs text-steel-500">{t('equipmentMgmt.code')}</p>
                    <p className="font-bold text-navy-800">{selectedItem.code}</p>
                  </div>
                  <div className="p-3 bg-steel-50 rounded-lg">
                    <p className="text-xs text-steel-500">{t('equipmentMgmt.category')}</p>
                    <p className="font-bold text-navy-800">{selectedItem.category}</p>
                  </div>
                </div>
                {selectedItem.dimensions && (
                  <div className="p-3 bg-steel-50 rounded-lg">
                    <p className="text-xs text-steel-500 mb-1">{t('equipmentMgmt.dimensions')}</p>
                    <p className="font-medium text-navy-800">
                      {selectedItem.dimensions.length_cm} × {selectedItem.dimensions.width_cm} × {selectedItem.dimensions.height_cm} cm
                    </p>
                  </div>
                )}
                {selectedItem.capacity && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-steel-50 rounded-lg">
                      <p className="text-xs text-steel-500">{t('equipmentMgmt.maxWeight')}</p>
                      <p className="font-bold text-navy-800">{selectedItem.capacity.max_weight_kg?.toLocaleString()} kg</p>
                    </div>
                    <div className="p-3 bg-steel-50 rounded-lg">
                      <p className="text-xs text-steel-500">{t('equipmentMgmt.maxVolume')}</p>
                      <p className="font-bold text-navy-800">{selectedItem.capacity.max_volume_cbm} cbm</p>
                    </div>
                  </div>
                )}
                {selectedItem.recommended_use?.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-navy-800 mb-2">{t('equipmentMgmt.recommendedUse')}</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedItem.recommended_use.map((use) => (
                        <Badge key={`use-${use}`} variant="outline">{use}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {selectedItem.restrictions?.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-navy-800 mb-2">{t('equipmentMgmt.restrictions')}</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedItem.restrictions.map((r) => (
                        <Badge key={`restrict-${r}`} variant="outline" className="text-red-600 border-red-300">{r}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setShowDetails(false)}>{t('common.close')}</Button>
                <Button onClick={() => { setShowDetails(false); openEditDialog(selectedItem); }}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  {t('common.edit')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('equipmentMgmt.addEquipment')}</DialogTitle>
          </DialogHeader>
          <EquipmentForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreate} disabled={saving} className="btn-accent">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('equipmentMgmt.editEquipment')}</DialogTitle>
          </DialogHeader>
          <EquipmentForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleUpdate} disabled={saving} className="btn-accent">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {t('common.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('equipmentMgmt.deleteEquipment')}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t('equipmentMgmt.confirmDelete')} &ldquo;{itemToDelete?.name}&rdquo;
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EquipmentManagement;
